/**
 * Device Detection Utility for Mobile App Promotion
 * Detects device type and app store compatibility
 */

export const DeviceInfo = {
  MOBILE_PHONE: 'mobile_phone',
  TABLET: 'tablet', 
  DESKTOP: 'desktop',
  IOS: 'ios',
  ANDROID: 'android',
  OTHER: 'other'
};

/**
 * Detect device type and operating system
 */
export const detectDevice = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() || '';
  
  // Detect operating system
  let os = DeviceInfo.OTHER;
  if (/iphone|ipad|ipod/.test(userAgent) || /mac/.test(platform)) {
    os = DeviceInfo.IOS;
  } else if (/android/.test(userAgent)) {
    os = DeviceInfo.ANDROID;
  }
  
  // Detect device type
  let deviceType = DeviceInfo.DESKTOP;
  
  // Check for mobile phones
  if (/mobile|android|iphone|ipod|blackberry|opera mini|iemobile|wpdesktop/.test(userAgent)) {
    deviceType = DeviceInfo.MOBILE_PHONE;
  }
  // Check for tablets (including iPad)
  else if (/tablet|ipad|playbook|silk/.test(userAgent) || 
           (/android/.test(userAgent) && !/mobile/.test(userAgent))) {
    deviceType = DeviceInfo.TABLET;
  }
  
  // Additional iPad detection (newer iPads report as desktop)
  if (/ipad/.test(userAgent) || (platform === 'macintel' && navigator.maxTouchPoints > 1)) {
    deviceType = DeviceInfo.TABLET;
    os = DeviceInfo.IOS;
  }
  
  return {
    deviceType,
    os,
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    isMobile: deviceType === DeviceInfo.MOBILE_PHONE,
    isTablet: deviceType === DeviceInfo.TABLET,
    isDesktop: deviceType === DeviceInfo.DESKTOP,
    isIOS: os === DeviceInfo.IOS,
    isAndroid: os === DeviceInfo.ANDROID,
    canDownloadApp: os === DeviceInfo.IOS || os === DeviceInfo.ANDROID,
    preferredStore: os === DeviceInfo.IOS ? 'apple' : os === DeviceInfo.ANDROID ? 'google' : null
  };
};

/**
 * Get app store URLs
 */
export const getAppStoreUrls = () => {
  return {
    apple: 'https://apps.apple.com/ng/app/m-mart/id6751410231',
    google: 'https://play.google.com/store/apps/details?id=com.mmartplus.app&pcampaignid=web_share'
  };
};

/**
 * Check if user should see app promotion popup
 */
export const shouldShowAppPromotion = () => {
  const device = detectDevice();
  const lastShown = localStorage.getItem('mmart-app-promo-shown');
  const dismissedUntil = localStorage.getItem('mmart-app-promo-dismissed-until');
  
  // Don't show if dismissed recently (24 hours)
  if (dismissedUntil && new Date(dismissedUntil) > new Date()) {
    return false;
  }
  
  // Don't show if already shown today
  if (lastShown && new Date(lastShown).toDateString() === new Date().toDateString()) {
    return false;
  }
  
  // FOR LOCAL TESTING: Allow desktop users in development mode
  const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  
  if (isDevelopment) {
    // In development, show for all devices (desktop, mobile, tablet)
    return true;
  }
  
  // In production, show for all devices (mobile, tablet, desktop) 
  // Desktop users can still access app stores via QR codes or direct links
  return true;
};

/**
 * Mark app promotion as shown
 */
export const markAppPromotionShown = () => {
  localStorage.setItem('mmart-app-promo-shown', new Date().toISOString());
};

/**
 * Dismiss app promotion for specified duration
 */
export const dismissAppPromotion = (hours = 24) => {
  const dismissUntil = new Date();
  dismissUntil.setHours(dismissUntil.getHours() + hours);
  localStorage.setItem('mmart-app-promo-dismissed-until', dismissUntil.toISOString());
};

export default {
  detectDevice,
  getAppStoreUrls,
  shouldShowAppPromotion,
  markAppPromotionShown,
  dismissAppPromotion,
  DeviceInfo
};
